/**
 * Icon Registry Pattern for Attack Graph
 *
 * Extensible registry system that allows adding new icon sources
 * without modifying the core AttackGraphIcon component.
 *
 * @example
 * ```tsx
 * // Register a new icon source
 * iconRenderers.set('mySource', {
 *   render: (name, props) => <MyIcon name={name} {...props} />,
 *   validate: (name) => myIcons.has(name),
 *   getIcon: (name) => myIcons.get(name) ?? null,
 *   getAll: () => [...myIcons.entries()].map(([id, component]) => ({
 *     id: `mySource:${id}`,
 *     component,
 *     label: id,
 *     category: 'mySource',
 *   })),
 * });
 *
 * // Get all icons for IconPicker
 * const allIcons = getAllIcons();
 * ```
 */

import type { IconSource } from '@/sections/attacks/attackPaths/components/icons/types';

// ============================================================================
// Re-export types and Map from isolated module
// ============================================================================
// CRITICAL: These exports must come BEFORE the registry imports below.
// The isolated module has no dependencies on registry files, breaking
// the circular dependency chain.

export {
  type IconDefinition,
  type IconRenderer,
  iconRenderers,
  type IconRenderProps,
} from './icon-renderers-map';

// ============================================================================
// Registry Imports - Self-register on import
// ============================================================================
// CRITICAL: These imports must come AFTER the re-exports above.
// Each registry imports from icon-renderers-map.ts (not this file),
// so they can safely access iconRenderers without circular dependency.

import './aws.registry';
import './azure.registry';
import './gcp.registry';
import './ad.registry';
import './brand.registry';
import './cicd.registry';
import './custom.registry';
import './iconify.registry';
import './infrastructure.registry';
import './iot.registry';
import './lucide.registry';
import './social.registry';
import './web.registry';

import type { IconDefinition } from './icon-renderers-map';
// ============================================================================
// Helper Functions
// ============================================================================
import { iconRenderers } from './icon-renderers-map';

/**
 * Get all icons across all registered sources.
 *
 * Used by IconPicker to enumerate all available icons.
 *
 * @returns Array of all icon definitions from all sources
 */
export function getAllIcons(): IconDefinition[] {
  const allIcons: IconDefinition[] = [];
  iconRenderers.forEach(renderer => {
    allIcons.push(...renderer.getAll());
  });
  return allIcons;
}

/**
 * Get icons from a specific source.
 *
 * @param source - Icon source to get icons from
 * @returns Array of icon definitions from that source
 */
export function getIconsBySource(source: IconSource): IconDefinition[] {
  return iconRenderers.get(source)?.getAll() ?? [];
}
